import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpErrorContainerModel } from '../00_modules/core/models/httpErrors/http-error-container.model';
import { HttpSapErrorModel } from '../00_modules/core/models/httpErrors/http-sap-error.model';
import { ResponseModel } from '../00_modules/core/models/response.model';
import { FieldValidationResponseMessageModel } from '../00_modules/core/models/responseMessages/field-validation-response-message.model';
import { ResponseMessageTypeEnum } from '../00_modules/core/models/responseMessages/response-message-type.enum';
import { ResponseMessage } from '../00_modules/core/models/responseMessages/response-message.interface';
import { ResponseMessageModel } from '../00_modules/core/models/responseMessages/response-message.model';
import { AppMessageService } from '../00_modules/core/services/appMessage.service';
import { NotificationService } from '../00_modules/core/services/notification.service';
import { ValidationError, ValidationResult } from '../00_modules/core/services/ValidationResult';
import { UiFacade } from '../store/facade/ui.facade';

@Injectable()
export class NotificationInterceptor implements HttpInterceptor {
    constructor(
        private notificationService: NotificationService,
        private appMessageService: AppMessageService,
        private uiFacade: UiFacade,
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            map(event => {
                if (event instanceof HttpResponse) {
                    if (event.body?.type === ResponseMessageTypeEnum.critical) {
                        this.handleCriticalMessages([event.body]);
                    } else {
                        let isResponseModel = Object.prototype.hasOwnProperty.call(event.body, 'payload') && Object.prototype.hasOwnProperty.call(event.body, 'messages');
                        if (isResponseModel) {
                            return this.handleResponse(event as HttpResponse<ResponseModel>);
                        }
                    }
                }
                return event;
            }),
        );
    }

    private handleResponse(event: HttpResponse<ResponseModel>): HttpEvent<any> {
        let httpErrorContainer = new HttpErrorContainerModel();

        if (event.body.showOnlyOnSite) {
            throw event;
        }

        this.handleInfoMessages(event.body.messages);
        this.handleSuccessMessages(event.body.messages);
        this.handleWarningMessages(event.body.messages);
        this.handleErrorMessages(event.body.messages);
        this.handleSapErrorMessages(event.body.messages, httpErrorContainer);
        this.handleFieldValidationErrorMessages(event.body.messages);

        if (httpErrorContainer.hasErrors()) throw httpErrorContainer;

        return this.removeResponseWrapper(event);
    }

    private removeResponseWrapper(event: HttpResponse<ResponseModel>): HttpResponse<any> {
        return event.clone({ body: event.body.payload });
    }

    private handleCriticalMessages(messages: ResponseMessage[]) {
        let criticalMessages: ResponseMessage[] = messages.filter(message => message.type === ResponseMessageTypeEnum.critical);
        if (criticalMessages.length > 0) {
            let firstCriticalMessage: ResponseMessage = criticalMessages[0];
            this.notificationService.error(firstCriticalMessage.key ?? firstCriticalMessage.message);
            this.uiFacade.setIsCriticalError(true);
            this.uiFacade.setCriticalErrorKey(firstCriticalMessage.key);
        }
    }

    private handleFieldValidationErrorMessages(messages: ResponseMessage[]) {
        let fieldValidationErrorMessages: FieldValidationResponseMessageModel[] = messages
            .filter(message => message.type === ResponseMessageTypeEnum.fieldValidationError)
            .map(message => message as FieldValidationResponseMessageModel);

        if (fieldValidationErrorMessages.length > 0) {
            this.notificationService.error('COMMON_FIELD_VALIDATION_ERROR_TEXT');

            let validationResult = new ValidationResult();

            validationResult.isValid = false;
            validationResult.orderNumber = fieldValidationErrorMessages[0].orderNumber;
            validationResult.orderState = fieldValidationErrorMessages[0].orderState;

            validationResult.errors = [];
            validationResult.errors.push(
                ...fieldValidationErrorMessages.map(message => {
                    let validationError = new ValidationError();

                    validationError.key = message.key;
                    validationError.message = message.message;
                    validationError.path = message.path;
                    validationError.section = message.section;
                    validationError.listElement = message.listElement;
                    validationError.additionalInfo = message.additionalInfo;

                    return validationError;
                }),
            );

            this.appMessageService.setValidationMessages(validationResult);
        }
    }

    private handleInfoMessages(messages: ResponseMessage[]) {
        let infoMessages: ResponseMessageModel[] = messages.filter(message => message.type === ResponseMessageTypeEnum.info);

        if (infoMessages.length > 0) {
            let firstInfoMessage: ResponseMessage = infoMessages[0];
            this.notificationService.infoTranslate(firstInfoMessage.key ?? firstInfoMessage.message);
        }
    }

    private handleSuccessMessages(messages: ResponseMessage[]) {
        let successMessages: ResponseMessageModel[] = messages.filter(message => message.type === ResponseMessageTypeEnum.success);

        if (successMessages.length > 0) {
            let firstSuccessMessage: ResponseMessage = successMessages[0];
            this.notificationService.successTranslate(firstSuccessMessage.key ?? firstSuccessMessage.message);
        }
    }

    private handleWarningMessages(messages: ResponseMessage[]) {
        let warningMessages: ResponseMessageModel[] = messages.filter(message => message.type === ResponseMessageTypeEnum.warning);

        if (warningMessages.length > 0) {
            let firstWarningMessage: ResponseMessage = warningMessages[0];
            this.notificationService.warningTranslate(firstWarningMessage.key ?? firstWarningMessage.message);
        }
    }

    private handleErrorMessages(messages: ResponseMessage[]) {
        let errorMessages: ResponseMessageModel[] = messages.filter(message => message.type === ResponseMessageTypeEnum.error);

        if (errorMessages.length > 0) {
            let firstErrorMessage = errorMessages[0];

            if (firstErrorMessage.key && firstErrorMessage.key?.indexOf('NOTTRANSLATABLE') > 0) {
                this.notificationService.errorTranslateWithAdditionalInfos(firstErrorMessage.key.toUpperCase(), firstErrorMessage.code, firstErrorMessage.message);
            } else if (!firstErrorMessage.key && firstErrorMessage.message) {
                this.notificationService.error(firstErrorMessage.message);
            } else {
                if (firstErrorMessage.arguments && firstErrorMessage.arguments.length > 0) {
                    let firstParam: string = null;
                    let secondParam: string = null;
                    let thirdParam: string = null;
                    let fourthParam: string = null;

                    if (firstErrorMessage.arguments.length > 3) fourthParam = firstErrorMessage.arguments[3];
                    if (firstErrorMessage.arguments.length > 2) thirdParam = firstErrorMessage.arguments[2];
                    if (firstErrorMessage.arguments.length > 1) secondParam = firstErrorMessage.arguments[1];
                    if (firstErrorMessage.arguments.length > 0) firstParam = firstErrorMessage.arguments[0];

                    this.notificationService.errorTranslateWithAdditionalInfos(firstErrorMessage.key.toUpperCase(), firstParam, secondParam, thirdParam, fourthParam);
                } else {
                    this.notificationService.errorTranslate(firstErrorMessage.key.toUpperCase());
                }
            }
        }
    }

    private handleSapErrorMessages(messages: ResponseMessage[], httpErrorContainer: HttpErrorContainerModel) {
        let sapErrorMessages: ResponseMessageModel[] = messages.filter(message => message.type === ResponseMessageTypeEnum.sapError);

        if (sapErrorMessages.length > 0) {
            httpErrorContainer.sapValidationErrors.push(...sapErrorMessages.map(message => new HttpSapErrorModel(message.key, message.message)));
        }
    }
}
